import type { InferenceRecord, InferenceResultType } from './inference-results-data';

export const EXPLORER_PATH = '/graphclaw-hub/inferences';
export type ExplorerLocation = {
	type: InferenceResultType;
	node?: string;
	source?: string;
	row?: string;
};

export const readExplorerLocation = (search: string): ExplorerLocation => {
	const params = new URLSearchParams(search);
	const type = params.get('type');
	return {
		type:
			type === 'project' || type === 'decision' || type === 'team' || type === 'artifact'
				? type
				: 'all',
		node: params.get('node') || undefined,
		source: params.get('source') || undefined,
		row: params.get('row') || undefined,
	};
};

export const explorerUrl = (location: ExplorerLocation, details = false): string => {
	const params = new URLSearchParams();
	if (location.type !== 'all') {
		params.set('type', location.type);
	}
	if (location.node) {
		params.set('node', location.node);
		if (location.source) {
			params.set('source', location.source);
		}
		if (location.row) {
			params.set('row', location.row);
		}
	}
	const query = params.toString();
	return `${EXPLORER_PATH}${details ? '/details' : ''}${query ? `?${query}` : ''}`;
};

export const recordLocation = (
	type: InferenceResultType,
	record: InferenceRecord,
): ExplorerLocation => ({
	type,
	node: record.ari,
	...(record.type === 'artifact' ? { source: record.source?.ari, row: record.id } : {}),
});

export const matchesExplorerLocation = (
	record: InferenceRecord,
	location: ExplorerLocation,
): boolean =>
	record.ari === location.node &&
	(!location.source || record.source?.ari === location.source) &&
	(!location.row || record.id === location.row);

/** Append new rows without moving existing ones; merge connections split across query batches. */
export const appendInferenceRecords = (
	current: InferenceRecord[],
	next: InferenceRecord[],
): InferenceRecord[] => {
	const records = new Map(current.map((record) => [record.id, record]));
	next.forEach((record) => {
		const previous = records.get(record.id);
		const references = new Map(
			previous?.references.map((reference) => [
				`${reference.ari}:${reference.relationshipType ?? ''}:${
					reference.relationshipDirection ?? ''
				}`,
				reference,
			]),
		);
		record.references.forEach((reference) =>
			references.set(
				`${reference.ari}:${reference.relationshipType ?? ''}:${
					reference.relationshipDirection ?? ''
				}`,
				reference,
			),
		);
		records.set(record.id, { ...previous, ...record, references: [...references.values()] });
	});
	return [...records.values()];
};

export const navigateExplorer = (url: string, replace = false): void => {
	globalThis.history[replace ? 'replaceState' : 'pushState'](null, '', url);
	globalThis.dispatchEvent(new PopStateEvent('popstate'));
};
